// Brush settings interface matching Photoshop's Brush panel.
// All ranges are 0..1 unless noted otherwise.

export interface BrushSettings {
  // Basic
  size: number;          // brush diameter in pixels
  hardness: number;      // 0..1 (soft edge)
  opacity: number;       // 0..1
  flow: number;          // 0..1 (paint buildup rate)
  spacing: number;       // 0..1 (fraction of size; 0.25 = 25% of size)

  // Shape Dynamics
  sizeJitter: number;    // 0..1
  sizeJitterControl: "off" | "pen-pressure" | "fade" | "pen-tilt";
  angleJitter: number;   // 0..1
  angleJitterControl: "off" | "direction" | "initial-direction" | "pen-tilt";
  roundnessJitter: number; // 0..1
  minRoundness: number;  // 0..1

  // Scattering
  scatter: number;       // fraction of brush radius
  scatterBothAxes: boolean;
  count: number;         // stamps per spacing interval (1..16)
  countJitter: number;   // 0..1

  // Color Dynamics
  fgBgJitter: number;    // 0..1 (foreground/background color mixing)
  hueJitter: number;     // 0..1 (hue variation)
  purity: number;        // -1..1 (color saturation control)
  colorDynamicsPerTip: boolean;

  // Transfer
  opacityJitter: number; // 0..1
  opacityJitterControl: "off" | "pen-pressure" | "fade";
  flowJitter: number;    // 0..1
  flowJitterControl: "off" | "pen-pressure" | "fade";

  // Special
  wetEdges: boolean;     // watercolor edge buildup
  airbrush: boolean;     // spray buildup over time
  airbrushRate: number;  // 0..1 (paint buildup speed)

  // Brush blend mode (Photoshop brush options bar)
  blendMode: string;     // "normal", "dissolve", "behind", "clear", etc.

  // Brush options bar — stroke smoothing (Photoshop's Smoothing slider)
  smoothing?: number;    // 0..1 (higher = smoother stroke; on by default at 0.1)

  // Painting symmetry (Photoshop's Symmetry button on the brush options bar)
  symmetry?: boolean;
}

export function defaultBrushSettings(): BrushSettings {
  return {
    size: 30,
    hardness: 0.5,
    opacity: 1,
    flow: 1,
    spacing: 0.25,

    sizeJitter: 0,
    sizeJitterControl: "off",
    angleJitter: 0,
    angleJitterControl: "off",
    roundnessJitter: 0,
    minRoundness: 0.25,

    scatter: 0,
    scatterBothAxes: true,
    count: 1,
    countJitter: 0,

    fgBgJitter: 0,
    hueJitter: 0,
    purity: 0,
    colorDynamicsPerTip: false,

    opacityJitter: 0,
    opacityJitterControl: "off",
    flowJitter: 0,
    flowJitterControl: "off",

    wetEdges: false,
    airbrush: false,
    airbrushRate: 0.1,

    blendMode: "normal",

    smoothing: 0.1,
    symmetry: false,
  };
}

/**
 * i76 — Photoshop's Brush options bar "Smoothing" slider (BrushSettings.
 * smoothing) damps the raw pointer position toward the previously-used
 * stroke point before it becomes the next stroke point, producing a
 * smoother (laggier) line at higher values. This is a standard exponential
 * low-pass / lag filter: the returned point sits `(1 - smoothing)` of the
 * way from the last point to the raw sample, so smoothing=0 (or undefined)
 * passes the raw point straight through, and smoothing→1 makes the stroke
 * lag further and further behind the actual pointer position.
 *
 * Pure function — the caller (BrushTool.onPointerMove) is responsible for
 * tracking the running "last" point across pointer-move events and feeding
 * the smoothed result back in as `lastX`/`lastY` on the next call.
 */
export function applyStrokeSmoothing(
  lastX: number,
  lastY: number,
  rawX: number,
  rawY: number,
  smoothing: number | undefined,
): { x: number; y: number } {
  const s = Math.max(0, Math.min(1, smoothing ?? 0));
  if (s <= 0) return { x: rawX, y: rawY };
  return {
    x: lastX + (rawX - lastX) * (1 - s),
    y: lastY + (rawY - lastY) * (1 - s),
  };
}

/**
 * Compute a jittered value given a base, jitter amount (0..1), and control mode.
 * When control is "off", uses Math.random() directly.
 * When control is a pen/tilt mode, the controlValue (0..1) modulates the jitter.
 */
export function applyJitter(
  base: number,
  jitter: number,
  control: "off" | "pen-pressure" | "fade" | "pen-tilt" | "direction" | "initial-direction",
  controlValue: number = 1,
): number {
  if (jitter <= 0) return base;
  const effectiveJitter = control === "off"
    ? jitter
    : jitter * (1 - controlValue);
  return base * (1 - effectiveJitter + effectiveJitter * Math.random());
}

/**
 * Interpolate between foreground and background colors based on jitter.
 */
export function mixFgBg(
  fgR: number, fgG: number, fgB: number,
  bgR: number, bgG: number, bgB: number,
  jitter: number,
): [number, number, number] {
  if (jitter <= 0) return [fgR, fgG, fgB];
  const t = Math.random() * jitter;
  return [
    Math.round(fgR * (1 - t) + bgR * t),
    Math.round(fgG * (1 - t) + bgG * t),
    Math.round(fgB * (1 - t) + bgB * t),
  ];
}

/**
 * Apply hue jitter to an RGB color.
 * Converts to HSL, shifts hue, converts back.
 */
export function applyHueJitter(
  r: number, g: number, b: number,
  jitter: number,
): [number, number, number] {
  if (jitter <= 0) return [r, g, b];
  const [h, s, l] = rgbToHsl(r, g, b);
  const hueShift = (Math.random() - 0.5) * 2 * jitter * 360;
  const newH = ((h * 360 + hueShift) % 360 + 360) % 360 / 360;
  const [nr, ng, nb] = hslToRgb(newH, s, l);
  return [Math.round(nr), Math.round(ng), Math.round(nb)];
}

/**
 * Apply purity (saturation) adjustment.
 * purity > 0 increases saturation, < 0 decreases it.
 */
export function applyPurity(
  r: number, g: number, b: number,
  purity: number,
): [number, number, number] {
  if (purity === 0) return [r, g, b];
  const [h, s, l] = rgbToHsl(r, g, b);
  const newS = purity > 0
    ? Math.min(1, s + purity * (1 - s))
    : Math.max(0, s + purity * s);
  const [nr, ng, nb] = hslToRgb(h, newS, l);
  return [Math.round(nr), Math.round(ng), Math.round(nb)];
}

// ── HSL conversion helpers ──

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [r * 255, g * 255, b * 255];
}
